import { useState, useMemo } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';

interface Unit {
  id: string;
  label: string;
  /** factor to base */
  factor: number;
  /** optional explicit converter pair */
  to?: (v: number) => number;
  from?: (v: number) => number;
}

interface Category {
  id: string;
  name: string;
  base: string;
  units: Unit[];
}

const CATEGORIES: Category[] = [
  {
    id: 'length',
    name: 'Length',
    base: 'm',
    units: [
      { id: 'mm', label: 'Millimeter', factor: 0.001 },
      { id: 'cm', label: 'Centimeter', factor: 0.01 },
      { id: 'm', label: 'Meter', factor: 1 },
      { id: 'km', label: 'Kilometer', factor: 1000 },
      { id: 'in', label: 'Inch', factor: 0.0254 },
      { id: 'ft', label: 'Foot', factor: 0.3048 },
      { id: 'yd', label: 'Yard', factor: 0.9144 },
      { id: 'mi', label: 'Mile', factor: 1609.344 },
    ],
  },
  {
    id: 'mass',
    name: 'Mass',
    base: 'kg',
    units: [
      { id: 'mg', label: 'Milligram', factor: 1e-6 },
      { id: 'g', label: 'Gram', factor: 1e-3 },
      { id: 'kg', label: 'Kilogram', factor: 1 },
      { id: 't', label: 'Metric ton', factor: 1000 },
      { id: 'oz', label: 'Ounce', factor: 0.0283495 },
      { id: 'lb', label: 'Pound', factor: 0.453592 },
    ],
  },
  {
    id: 'temperature',
    name: 'Temperature',
    base: 'C',
    units: [
      { id: 'C', label: 'Celsius', factor: 1, to: (v) => v, from: (v) => v },
      { id: 'F', label: 'Fahrenheit', factor: 1, to: (v) => (v * 9) / 5 + 32, from: (v) => ((v - 32) * 5) / 9 },
      { id: 'K', label: 'Kelvin', factor: 1, to: (v) => v + 273.15, from: (v) => v - 273.15 },
    ],
  },
  {
    id: 'volume',
    name: 'Volume',
    base: 'l',
    units: [
      { id: 'ml', label: 'Milliliter', factor: 0.001 },
      { id: 'l', label: 'Liter', factor: 1 },
      { id: 'm3', label: 'Cubic meter', factor: 1000 },
      { id: 'tsp', label: 'Teaspoon', factor: 0.00492892 },
      { id: 'tbsp', label: 'Tablespoon', factor: 0.0147868 },
      { id: 'cup', label: 'Cup (US)', factor: 0.236588 },
      { id: 'pint', label: 'Pint (US)', factor: 0.473176 },
      { id: 'gal', label: 'Gallon (US)', factor: 3.78541 },
    ],
  },
  {
    id: 'area',
    name: 'Area',
    base: 'm2',
    units: [
      { id: 'm2', label: 'm²', factor: 1 },
      { id: 'km2', label: 'km²', factor: 1e6 },
      { id: 'ha', label: 'Hectare', factor: 10000 },
      { id: 'acre', label: 'Acre', factor: 4046.86 },
      { id: 'sqft', label: 'Square feet', factor: 0.092903 },
    ],
  },
  {
    id: 'speed',
    name: 'Speed',
    base: 'mps',
    units: [
      { id: 'mps', label: 'm/s', factor: 1 },
      { id: 'kph', label: 'km/h', factor: 1 / 3.6 },
      { id: 'mph', label: 'mph', factor: 0.44704 },
      { id: 'kn', label: 'Knots', factor: 0.514444 },
    ],
  },
  {
    id: 'data',
    name: 'Data',
    base: 'B',
    units: [
      { id: 'b', label: 'Bit', factor: 0.125 },
      { id: 'B', label: 'Byte', factor: 1 },
      { id: 'KB', label: 'Kilobyte', factor: 1024 },
      { id: 'MB', label: 'Megabyte', factor: 1024 ** 2 },
      { id: 'GB', label: 'Gigabyte', factor: 1024 ** 3 },
      { id: 'TB', label: 'Terabyte', factor: 1024 ** 4 },
    ],
  },
  {
    id: 'time',
    name: 'Time',
    base: 's',
    units: [
      { id: 'ms', label: 'Millisecond', factor: 0.001 },
      { id: 's', label: 'Second', factor: 1 },
      { id: 'min', label: 'Minute', factor: 60 },
      { id: 'h', label: 'Hour', factor: 3600 },
      { id: 'day', label: 'Day', factor: 86400 },
      { id: 'week', label: 'Week', factor: 604800 },
    ],
  },
];

function convert(value: number, from: Unit, to: Unit): number {
  if (from.to && from.from && to.to && to.from) {
    return to.to(from.from(value));
  }
  return (value * from.factor) / to.factor;
}

export function UnitConverter() {
  const [catId, setCatId] = useState('length');
  const cat = CATEGORIES.find((c) => c.id === catId)!;
  const [from, setFrom] = useState(cat.units[0].id);
  const [to, setTo] = useState(cat.units[1].id);
  const [value, setValue] = useState(1);

  const result = useMemo(() => {
    const f = cat.units.find((u) => u.id === from)!;
    const t = cat.units.find((u) => u.id === to)!;
    return convert(value, f, t);
  }, [value, from, to, cat]);

  return (
    <div>
      <ToolHeader
        toolId="unit-converter"
        title="Unit converter"
        description="Length, mass, temperature, volume, area, speed, data, time — all the everyday units."
      />

      <Tabs
        value={catId}
        onValueChange={(v) => {
          setCatId(v);
          const next = CATEGORIES.find((c) => c.id === v)!;
          setFrom(next.units[0].id);
          setTo(next.units[1].id);
        }}
      >
        <TabsList className="mb-4 flex-wrap !h-auto">
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.id} value={c.id}>
              {c.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <div className="space-y-2">
            <Field label="From">
              <Select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                options={cat.units.map((u) => ({ value: u.id, label: u.label }))}
              />
            </Field>
            <Input
              type="number"
              value={value}
              onChange={(e) => setValue(Number(e.target.value) || 0)}
              className="font-mono text-lg !h-12"
            />
          </div>

          <button
            onClick={() => {
              const tmp = from;
              setFrom(to);
              setTo(tmp);
            }}
            className="size-10 rounded-lg border border-border hover:border-border-strong hover:bg-surface-hover flex items-center justify-center self-end mb-1.5 transition"
          >
            <ArrowLeftRight size={14} />
          </button>

          <div className="space-y-2">
            <Field label="To">
              <Select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                options={cat.units.map((u) => ({ value: u.id, label: u.label }))}
              />
            </Field>
            <div className="!h-12 px-3 flex items-center rounded-lg bg-bg-inset border border-border font-mono text-lg tabular-nums">
              {result.toLocaleString(undefined, { maximumFractionDigits: 6 })}
            </div>
          </div>
        </div>
      </Card>

      {/* Quick conversions */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cat.units.map((u) => (
          <Card key={u.id} className="!p-3">
            <div className="text-2xs uppercase tracking-wider text-fg-subtle">{u.label}</div>
            <div className="text-sm font-mono mt-0.5 tabular-nums">
              {convert(value, cat.units.find((x) => x.id === from)!, u).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default UnitConverter;
